import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import SEO from '../../components/SEO/SEO';
import { toast } from 'react-toastify';
import cupIcon from '../../assets/cupIcon.svg';

import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

import { useAuthUser } from '../../hooks/useAuthUser';
import { RoutineContext } from '../../contexts/RoutineContext';
import AppLoader from '../../components/AppLoader/AppLoader';

import style from './RoutinePage.module.css';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import UpgradePrompt from '../../components/UpgradePrompt/UpgradePrompt';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import Timer from '../../components/Timer/Timer';
import LeaderBoardsModal from '../../components/LeaderBoardsModal/LeaderBoardsModal';
import Modal from '../../components/Modal/Modal';
import WorkoutSession from '../../components/WorkoutSession/WorkoutSession';
import { AdaptationBanner, AdaptationHistory } from '../../components/AdaptationBanner/AdaptationBanner';
import { checkSubscription } from '../../utils/checkSubscription';
import { encodePathSegment, requestWithApiFallback } from '../../utils/apiRequest';
import done from '../../assets/done.mp3';
import { UserContext } from '../../contexts/UserContext';
import VerifiedBadge from '../../components/VerifiedBadge/VerifiedBadge';
import {
	MdFavorite, MdFavoriteBorder, MdShare, MdLock, MdLockOpen,
	MdSend, MdAutoAwesome, MdPersonAdd, MdChatBubbleOutline, MdCheck, MdBookmark, MdBookmarkBorder, MdNotifications,
	MdPlayArrow, MdMail, MdAdd, MdKeyboardArrowUp, MdKeyboardArrowDown, MdSwapVert, MdClose, MdDelete
} from 'react-icons/md';

const RoutinePage = () => {
	const { userID, routineName } = useParams();
	const { user, isLoaded } = useAuthUser();
	const { dbUser, isDbUserResolved } = useContext(UserContext);
	const [routine, setRoutine] = useState({});
	const [routineUser, setRoutineUser] = useState({});
	const [isRoutineLoading, setIsRoutineLoading] = useState(true);
	const [routineLoadError, setRoutineLoadError] = useState('');
	const [isLeaderboardsOpen, setIsLeaderboardsOpen] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();

	const [commentText, setCommentText] = useState('');
	const [showAllComments, setShowAllComments] = useState(false);
	const [shareURL, setShareURL] = useState('');
	const [copied, setCopied] = useState(false);
	const [isBookmarked, setIsBookmarked] = useState(false);
	const [showAISuggestions, setShowAISuggestions] = useState(false);
	const [aiSuggestions, setAiSuggestions] = useState('');
	const [aiLoading, setAiLoading] = useState(false);
	const [showInvite, setShowInvite] = useState(false);
	const [inviteSearch, setInviteSearch] = useState('');
	const [inviteResults, setInviteResults] = useState([]);
	const [failedAvatars, setFailedAvatars] = useState({});
	const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
	const [reminderDateTime, setReminderDateTime] = useState('');
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [sessionActive, setSessionActive] = useState(false);
	const [emailInvite, setEmailInvite] = useState('');
	const [emailInviteSending, setEmailInviteSending] = useState(false);
	const [pendingAdaptation, setPendingAdaptation] = useState(null);
	const [adaptationHistory, setAdaptationHistory] = useState([]);
	const [isApplyingAdaptation, setIsApplyingAdaptation] = useState(false);
	const [sessionStartTime, setSessionStartTime] = useState(null);
	const [isAddWorkoutOpen, setIsAddWorkoutOpen] = useState(false);
	const [newWorkoutType, setNewWorkoutType] = useState('');
	const [newWorkoutSets, setNewWorkoutSets] = useState('3');
	const [newWorkoutReps, setNewWorkoutReps] = useState('10');
	const [isEditMode, setIsEditMode] = useState(false);
	const [parsedSuggestions, setParsedSuggestions] = useState([]);
	const [suggestionPrompt, setSuggestionPrompt] = useState('');
	const hasAutoStartedRef = useRef(false);

	useEffect(() => {
		if (!isLoaded) return;
		if (!user) {
			const path = `/stryd/${userID}/${routineName}${location.search || ''}`;
			navigate(`/login?redirectTo=${encodeURIComponent(path)}`, { replace: true });
		}
	}, [isLoaded, user, userID, routineName, navigate, location.search]);

	const { currentXP, setCurrentXP, setCurrentProgress, currentProgress } = useContext(RoutineContext);

	const API_URL = process.env.REACT_APP_API_URL;
	const requestApi = useCallback((requestFactory) => requestWithApiFallback(API_URL, requestFactory), [API_URL]);

	useEffect(() => {
		const shouldAutoStart = new URLSearchParams(location.search).get('autostart') === '1';
		if (!shouldAutoStart || hasAutoStartedRef.current) return;
		if (!routine.workouts?.length || sessionActive) return;
		hasAutoStartedRef.current = true;
		setSessionActive(true);
		setSessionStartTime(Date.now());
	}, [location.search, routine.workouts, sessionActive]);

	const getRoutines = useCallback(async () => {
		if (!user?.id) return;
		setIsRoutineLoading(true);
		setRoutineLoadError('');
		try {
			const searchParams = new URLSearchParams(location.search);
			const shareCode = searchParams.get('share');
			const query = new URLSearchParams();
			query.set('viewerID', user.id);
			if (shareCode) query.set('share', shareCode);

			const res = await requestApi((base) => axios.get(
				`${base}/stryds/${encodePathSegment(userID)}/${encodePathSegment(routineName)}?${query.toString()}`
			));
			setRoutine(res.data || {});
		} catch (err) {
			console.error(err.message);
			setRoutine({});
			if (err?.response?.status === 403 && err?.response?.data?.error === 'blocked') {
				setRoutineLoadError(err.response.data.message || 'You have been blocked by this user.');
			} else {
				setRoutineLoadError('This routine is unavailable or private.');
			}
		} finally {
			setIsRoutineLoading(false);
		}
	}, [user?.id, requestApi, userID, routineName, location.search]);

	const getUser = useCallback(async () => {
		if (!user?.id) return;
		try {
			const res = await requestApi((base) => axios.get(`${base}/users/${encodePathSegment(userID)}`));
			setRoutineUser(res.data?.[0] || {});
		} catch (err) {
			console.error(err.message);
			setRoutineUser({});
		}
	}, [user?.id, requestApi, userID]);

	const getBookmarkStatus = useCallback(() => {
		if (!user?.id) return;
		axios.get(`${API_URL}/users/${user.id}/bookmarks`)
			.then(res => {
				const key = `${userID}:${routineName}`;
				const bookmarked = (res.data || []).some(bookmark => bookmark.type === 'routine' && bookmark.refID === key);
				setIsBookmarked(bookmarked);
			})
			.catch(() => {});
	}, [API_URL, user?.id, userID, routineName]);

	const joinRoutine = async (routineCreatorID, rName, uID, uName, profileImg) => {
		toast('joining routine');
		const userToAdd = { userID: uID, username: uName, profileImageLink: profileImg, userRoutineXP: 0 };
		const routineToAdd = {
			routineName: routine.routineName,
			estimatedTimeMinutes: routine.estimatedTimeMinutes,
			routineCreator: routineUser.username,
			routineCreatorUserID: routineUser.userID
		};

		setRoutine(prev => ({ ...prev, participants: [...(prev.participants || []), userToAdd] }));

		try {
			await axios.post(`${API_URL}/stryds/join-routine/${routineCreatorID}/${rName}`, userToAdd);
			await axios.post(`${API_URL}/stryds/joined-routines/${user.id}`, routineToAdd);
			toast('joined routine');
			axios.post(`${API_URL}/emails/send-email`, {
				to: routineUser.email,
				from: 'hello@calcium.fitness',
				subject: `Someone joined ${rName}`,
				html: `<h4>Hi, ${routineUser.username}.</h4><p>${user.username} just joined you in ${rName}.</p><p>Cheers!</p><p>Your friends at Calcium.</p>`
			}).catch(() => {});
		} catch {
			toast('an error occurred while joining routine');
			setRoutine(prev => ({
				...prev,
				participants: prev.participants.filter(p => p.userID !== uID)
			}));
		}
	};

	const leaveRoutine = async (routineCreatorID, rName, uID) => {
		toast('leaving routine');
		setRoutine(prev => ({ ...prev, participants: prev.participants.filter(p => p.userID !== uID) }));
		try {
			await axios.post(`${API_URL}/stryds/leave-routine/${routineCreatorID}/${rName}`, { userID: uID });
			await axios.delete(`${API_URL}/stryds/remove-routine/${uID}/${rName}`);
			toast('left routine');
		} catch {
			toast('an error occurred while leaving routine');
			getRoutines();
		}
	};

	const toggleLike = async () => {
		if (!user?.id) return;
		try {
			const res = await axios.post(`${API_URL}/stryds/like/${userID}/${routineName}`, {
				userID: user.id,
				username: dbUser?.username || user.firstName
			});
			setRoutine(prev => ({ ...prev, likes: res.data.likes }));
		} catch (err) {
			console.error('Like error:', err.message);
		}
	};

	const addComment = async () => {
		if (!commentText.trim() || !user?.id) return;
		try {
			const res = await requestApi((base) => axios.post(
				`${base}/stryds/comment/${encodePathSegment(userID)}/${encodePathSegment(routineName)}`,
				{
				userID: user.id,
				username: dbUser?.username || user.firstName,
				profileImageLink: user.profileImageUrl,
				text: commentText.trim()
				}
			));
			setRoutine(prev => ({ ...prev, comments: res.data.comments }));
			setCommentText('');
		} catch (err) {
			console.error('Comment error:', err.message);
			toast('Could not post comment right now');
		}
	};

	const togglePrivacy = async () => {
		try {
			const res = await axios.post(`${API_URL}/stryds/toggle-privacy/${userID}/${routineName}`);
			setRoutine(prev => ({ ...prev, isPrivate: res.data.isPrivate }));
			toast(res.data.isPrivate ? 'Routine set to private' : 'Routine set to public');
		} catch (err) {
			console.error('Privacy toggle error:', err.message);
		}
	};

	const generateShareLink = async () => {
		try {
			const res = await axios.post(`${API_URL}/stryds/share-link/${userID}/${routineName}`);
			setShareURL(res.data.shareURL);
			await navigator.clipboard.writeText(res.data.shareURL);
			setCopied(true);
			toast('Link copied!');
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error('Share error:', err.message);
		}
	};

	const toggleBookmark = async () => {
		if (!user?.id) return;
		try {
			const result = await axios.post(`${API_URL}/users/${user.id}/bookmarks/toggle`, {
				type: 'routine',
				refID: `${userID}:${routineName}`,
				refName: routine.routineName || routineName
			});
			setIsBookmarked(result.data.isBookmarked);
			toast(result.data.isBookmarked ? 'Routine bookmarked' : 'Bookmark removed');
		} catch (error) {
			console.error('Bookmark toggle error:', error.message);
		}
	};

	const toLocalDateTimeValue = (date) => {
		const pad = (num) => String(num).padStart(2, '0');
		return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
	};

	const openReminderModal = () => {
		const nextHour = new Date(Date.now() + (60 * 60 * 1000));
		nextHour.setMinutes(0, 0, 0);
		setReminderDateTime(toLocalDateTimeValue(nextHour));
		setIsReminderModalOpen(true);
	};

	const submitReminder = async () => {
		if (!user?.id) return;
		if (!reminderDateTime) {
			toast('Choose a date and time');
			return;
		}

		const parsedDate = new Date(reminderDateTime);
		if (Number.isNaN(parsedDate.getTime())) {
			toast('Invalid date format');
			return;
		}

		try {
			await axios.post(`${API_URL}/notifications/schedule-workout-reminder/${user.id}`, {
				routineName: routine.routineName || routineName,
				routineCreatorID: userID,
				scheduledFor: parsedDate.toISOString()
			});
			setIsReminderModalOpen(false);
			toast('Workout reminder scheduled');
		} catch (error) {
			console.error('Reminder schedule error:', error.message);
			toast('Could not schedule reminder');
		}
	};

	const deleteRoutine = async () => {
		toast('Deleting routine...');
		try {
			const response = await requestApi((base) => axios.delete(
				`${base}/stryds/delete/${encodePathSegment(userID)}/${encodePathSegment(routineName)}`
			));
			if (response.status === 200) {
				setIsDeleteModalOpen(false);
				toast('Routine deleted');
				navigate('/profile-page');
			}
		} catch {
			toast('An error occurred while deleting the routine');
		}
	};

	const shareCompletion = async () => {
		try {
			await axios.post(`${API_URL}/stryds/share-completion/${user.id}/${routineName}`, {
				xpEarned: currentXP,
				routineCreatorID: userID
			});
			toast('Shared to feed!');
		} catch (err) {
			console.error('Share completion error:', err.message);
		}
	};

	const getAISuggestions = async () => {
		if (!routine.workouts?.length) return;
		setAiLoading(true);
		setShowAISuggestions(true);
		setParsedSuggestions([]);
		try {
			const res = await axios.post(`${API_URL}/cali/suggest-workouts`, {
				routineName: routine.routineName,
				currentWorkouts: routine.workouts,
				interests: dbUser?.interests?.workoutInterests,
				goal: suggestionPrompt.trim()
			});
			const raw = res.data.message || '';
			setAiSuggestions(raw);
			const parsed = parseAISuggestionPayload(raw);
			setParsedSuggestions(parsed);
		} catch {
			setAiSuggestions('');
			setParsedSuggestions([]);
			toast('Could not generate suggestions right now. Try again later.');
		} finally {
			setAiLoading(false);
		}
	};

	const parseAISuggestionPayload = (text) => {
		const source = text || aiSuggestions;
		const match = source.match(/```suggestions\s*([\s\S]*?)```/);
		if (!match) return [];
		try {
			const parsed = JSON.parse(match[1]);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	};

	const getCleanAIText = () => {
		return aiSuggestions
			.replace(/```(?:suggestions|json|routine)?\s*[\s\S]*?```/g, '')
			.trim();
	};

	const addSingleSuggestion = async (suggestion) => {
		try {
			const res = await requestApi((base) => axios.post(
				`${base}/stryds/add-workouts/${encodePathSegment(userID)}/${encodePathSegment(routineName)}`,
				{ workouts: [suggestion] }
			));
			setRoutine(prev => ({ ...prev, workouts: res.data.workouts || prev.workouts }));
			setParsedSuggestions(prev => prev.filter(s => s.workoutType !== suggestion.workoutType));
			toast(`Added ${suggestion.workoutType}`);
		} catch (error) {
			console.error('Add workout error:', error.message);
			toast('Failed to add workout');
		}
	};

	const swapWorkoutWithSuggestion = async (suggestion) => {
		const existingIndex = workouts.findIndex(
			w => w.workoutType?.toLowerCase() === suggestion.replaces?.toLowerCase()
		);
		if (existingIndex === -1) {
			await addSingleSuggestion(suggestion);
			return;
		}
		try {
			const res = await requestApi((base) => axios.put(
				`${base}/stryds/swap-workout/${encodePathSegment(userID)}/${encodePathSegment(routineName)}/${existingIndex}`,
				{ newWorkout: suggestion }
			));
			setRoutine(prev => ({ ...prev, workouts: res.data.workouts || prev.workouts }));
			setParsedSuggestions(prev => prev.filter(s => s.workoutType !== suggestion.workoutType));
			toast(`Swapped ${suggestion.replaces} → ${suggestion.workoutType}`);
		} catch (error) {
			console.error('Swap error:', error.message);
			toast('Failed to swap workout');
		}
	};

	const addAllSuggestions = async () => {
		const toAdd = parsedSuggestions.filter(s => s.action !== 'swap');
		const toSwap = parsedSuggestions.filter(s => s.action === 'swap');

		for (const s of toSwap) {
			await swapWorkoutWithSuggestion(s);
		}
		if (toAdd.length > 0) {
			try {
				const res = await requestApi((base) => axios.post(
					`${base}/stryds/add-workouts/${encodePathSegment(userID)}/${encodePathSegment(routineName)}`,
					{ workouts: toAdd }
				));
				setRoutine(prev => ({ ...prev, workouts: res.data.workouts || prev.workouts }));
			} catch (error) {
				console.error('Add workouts error:', error.message);
				toast('Failed to add some workouts');
			}
		}
		setParsedSuggestions([]);
		toast('All suggestions applied');
	};

	const addWorkoutManually = async () => {
		if (!newWorkoutType.trim()) {
			toast('Enter an exercise name');
			return;
		}
		try {
			const res = await requestApi((base) => axios.post(
				`${base}/stryds/add-workouts/${encodePathSegment(userID)}/${encodePathSegment(routineName)}`,
				{ workouts: [{ workoutType: newWorkoutType.trim(), workoutSets: newWorkoutSets, workoutReps: newWorkoutReps }] }
			));
			setRoutine(prev => ({ ...prev, workouts: res.data.workouts || prev.workouts }));
			setNewWorkoutType('');
			setNewWorkoutSets('3');
			setNewWorkoutReps('10');
			setIsAddWorkoutOpen(false);
			toast('Workout added');
		} catch (error) {
			console.error('Add workout error:', error.message);
			toast('Failed to add workout');
		}
	};

	const removeWorkout = async (index) => {
		const removed = workouts[index];
		setRoutine(prev => ({
			...prev,
			workouts: prev.workouts.filter((_, i) => i !== index)
		}));
		try {
			await requestApi((base) => axios.delete(
				`${base}/stryds/remove-workout/${encodePathSegment(userID)}/${encodePathSegment(routineName)}/${index}`
			));
			toast(`Removed ${removed?.workoutType || 'workout'}`);
		} catch (error) {
			console.error('Remove workout error:', error.message);
			toast('Failed to remove workout');
			getRoutines();
		}
	};

	const moveWorkout = async (fromIndex, direction) => {
		const toIndex = fromIndex + direction;
		if (toIndex < 0 || toIndex >= workouts.length) return;
		const newWorkouts = [...workouts];
		[newWorkouts[fromIndex], newWorkouts[toIndex]] = [newWorkouts[toIndex], newWorkouts[fromIndex]];
		setRoutine(prev => ({ ...prev, workouts: newWorkouts }));
		try {
			await requestApi((base) => axios.put(
				`${base}/stryds/reorder-workouts/${encodePathSegment(userID)}/${encodePathSegment(routineName)}`,
				{ workouts: newWorkouts }
			));
		} catch (error) {
			console.error('Reorder error:', error.message);
			toast('Failed to reorder');
			getRoutines();
		}
	};

	const searchForInvite = async (query) => {
		if (userID !== user?.id) {
			setInviteSearch('');
			setInviteResults([]);
			toast('Only the routine creator can invite people.');
			return;
		}
		setInviteSearch(query);
		if (query.length < 2) { setInviteResults([]); return; }
		try {
			const res = await axios.get(`${API_URL}/search/people/${query}`);
			const filtered = (res.data || []).filter(p =>
				p.userID !== user?.id && !routine.participants?.some(part => part.userID === p.userID)
			);
			setInviteResults(filtered.slice(0, 5));
		} catch { setInviteResults([]); }
	};

	const sendInvite = async (toUser) => {
		if (userID !== user?.id) {
			toast('Only the routine creator can invite people.');
			return;
		}
		try {
			await axios.post(`${API_URL}/invites`, {
				fromUser: {
					userID: user.id,
					username: dbUser?.username || user.firstName,
					profileImageLink: user.profileImageUrl
				},
				toUserID: toUser.userID,
				routineRef: {
					routineName: routine.routineName,
					creatorID: userID,
					creatorUsername: routineUser.username,
					estimatedTimeMinutes: routine.estimatedTimeMinutes
				}
			});
			toast(`Invite sent to ${toUser.username}!`);
			setInviteResults(prev => prev.filter(p => p.userID !== toUser.userID));
		} catch (err) {
			toast(err.response?.data?.error || 'Failed to send invite');
		}
	};

	const sendEmailInvite = async () => {
		if (userID !== user?.id) {
			toast('Only the routine creator can invite people.');
			return;
		}
		const email = emailInvite.trim();
		if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			toast('Enter a valid email address');
			return;
		}
		setEmailInviteSending(true);
		try {
			await requestApi((base) => axios.post(`${base}/invites/email`, {
				fromUser: {
					userID: user.id,
					username: dbUser?.username || user.firstName,
					profileImageLink: user.profileImageUrl
				},
				toEmail: email,
				routineRef: {
					routineName: routine.routineName,
					creatorID: userID,
					creatorUsername: routineUser.username,
					estimatedTimeMinutes: routine.estimatedTimeMinutes
				}
			}));
			toast(`Invite sent to ${email}`);
			setEmailInvite('');
		} catch (err) {
			toast(err.response?.data?.error || 'Failed to send email invite');
		} finally {
			setEmailInviteSending(false);
		}
	};

	const checkForAdaptations = useCallback(async () => {
		if (!user?.id) return;
		try {
			const checkRes = await requestApi((base) => axios.get(`${base}/adaptation/check/${user.id}`));
			const data = checkRes.data;

			if (data.hasPendingAdaptation && data.pendingAdaptations?.length > 0) {
				const pending = data.pendingAdaptations.find(a => !a.appliedAt && !a.dismissed);
				if (pending) {
					setPendingAdaptation(pending);
					return;
				}
			}

			if (data.newTriggers?.length > 0) {
				const evalRes = await requestApi((base) => axios.post(`${base}/adaptation/evaluate/${user.id}`));
				if (evalRes.data?.adaptation) {
					setPendingAdaptation(evalRes.data.adaptation);
				}
			}
		} catch (err) {
			console.error('Adaptation check error:', err.message);
		}
	}, [user?.id, requestApi]);

	const fetchAdaptationHistory = useCallback(async () => {
		if (!user?.id) return;
		try {
			const res = await requestApi((base) => axios.get(`${base}/adaptation/history/${user.id}`));
			setAdaptationHistory(res.data || []);
		} catch (err) {
			console.error('Adaptation history error:', err.message);
		}
	}, [user?.id, requestApi]);

	const applyAdaptation = async () => {
		if (!pendingAdaptation || !user?.id) return;
		setIsApplyingAdaptation(true);
		try {
			await requestApi((base) => axios.post(
				`${base}/adaptation/apply/${user.id}/${pendingAdaptation._id}`
			));
			setPendingAdaptation(null);
			toast('Plan updated! Your routine has evolved.');
			getRoutines();
			fetchAdaptationHistory();
		} catch (err) {
			console.error('Apply adaptation error:', err.message);
			toast('Could not apply changes right now');
		} finally {
			setIsApplyingAdaptation(false);
		}
	};

	const dismissAdaptation = async () => {
		if (!pendingAdaptation || !user?.id) return;
		try {
			await requestApi((base) => axios.post(
				`${base}/adaptation/dismiss/${user.id}/${pendingAdaptation._id}`
			));
			setPendingAdaptation(null);
		} catch (err) {
			console.error('Dismiss adaptation error:', err.message);
		}
	};

	useEffect(() => {
		if (user) {
			getRoutines();
			getUser();
			getBookmarkStatus();
			checkForAdaptations();
			fetchAdaptationHistory();
		}
		return () => {
			setCurrentXP(0);
			setCurrentProgress(0);
		};
	}, [user, getRoutines, getUser, getBookmarkStatus, checkForAdaptations, fetchAdaptationHistory, setCurrentXP, setCurrentProgress]);

	useEffect(() => {
		if (currentProgress >= 100) {
			const audio = new Audio(done);
			audio.play().catch(() => {});

			if (user?.id) {
				const durationMinutes = sessionStartTime
					? Math.round((Date.now() - sessionStartTime) / 60000)
					: 0;

				axios.post(`${API_URL}/streaks/log-workout/${user.id}`, {
					routineId: routine?._id,
					routineName: routine?.routineName || routineName,
					routineCreatorID: userID,
					durationMinutes,
					estimatedMinutes: parseInt(routine?.estimatedTimeMinutes, 10) || 0,
					xpEarned: currentXP,
					workoutsCompleted: routine?.workouts?.length || 0,
				}).catch(() => {});
			}
		}
	}, [currentProgress]); // eslint-disable-line react-hooks/exhaustive-deps

	const workouts = routine?.workouts || [];
	const participants = routine?.participants || [];
	const isLiked = routine.likes?.some(l => l.userID === user?.id);
	const isOwner = userID === user?.id;
	const comments = routine.comments || [];
	const visibleComments = showAllComments ? comments : comments.slice(-3);
	const viewerReady = !user || isDbUserResolved;

	if (!isLoaded || (user && !viewerReady)) {
		return <AppLoader message='Loading routine...' />;
	}

	if (!user) {
		return <AppLoader message='Redirecting to login...' />;
	}

	if (user && viewerReady && !checkSubscription(dbUser)) {
		return (
			<UpgradePrompt
				heading="Viewing routines is a premium feature"
				description="Upgrade to unlock all routines and more."
				onUpgrade={() => navigate('/upgrade')}
			/>
		);
	}

	return (
		<div>
			<SEO
				title={routine?.routineName ? `${routine.routineName}` : 'Routine'}
				description={routine?.workouts?.length ? `Complete ${routine.workouts.length} workouts in ${routine.routineName} by ${routineUser?.username || 'a creator'} on Calcium.` : `View a routine on Calcium.`}
				path={`/stryd/${userID}/${routineName}`}
			/>

			{isLeaderboardsOpen && (
				<LeaderBoardsModal
					onClose={() => setIsLeaderboardsOpen(false)}
					people={routine.participants}
				/>
			)}

			{isReminderModalOpen && (
				<Modal
					onClose={() => setIsReminderModalOpen(false)}
					title='Schedule Workout Reminder'
					subtitle='Pick a time and we will nudge you to complete this routine.'
					maxWidth={520}
				>
					<div className={style.ModalForm}>
						<label className={style.ModalLabel} htmlFor='routine-reminder-datetime'>
							Date and time
						</label>
						<input
							id='routine-reminder-datetime'
							type='datetime-local'
							className={style.ModalDateInput}
							value={reminderDateTime}
							onChange={(e) => setReminderDateTime(e.target.value)}
						/>
						<p className={style.ModalHelperText}>
							Time is saved in your local timezone.
						</p>
						<div className={style.ModalActionsRow}>
							<button
								type='button'
								className={style.ModalGhostBtn}
								onClick={() => setIsReminderModalOpen(false)}
							>
								Cancel
							</button>
							<button type='button' className={style.ModalPrimaryBtn} onClick={submitReminder}>
								Schedule reminder
							</button>
						</div>
					</div>
				</Modal>
			)}

			{sessionActive && routine.workouts?.length > 0 && (
				<WorkoutSession
					workouts={routine.workouts}
					routineName={routineName}
					onEnd={() => setSessionActive(false)}
					onShareCompletion={shareCompletion}
					shareLink={shareURL || window.location.href}
				/>
			)}

			{isDeleteModalOpen && (
				<Modal
					onClose={() => setIsDeleteModalOpen(false)}
					title='Delete Routine'
					subtitle='This action is permanent and removes the routine for everyone.'
					maxWidth={520}
				>
					<div className={style.ModalForm}>
						<p className={style.ModalHelperText}>
							Are you sure you want to delete <strong>{routine.routineName || routineName}</strong>?
						</p>
						<div className={style.ModalActionsRow}>
							<button
								type='button'
								className={style.ModalGhostBtn}
								onClick={() => setIsDeleteModalOpen(false)}
							>
								Cancel
							</button>
							<button type='button' className={style.ModalDangerBtn} onClick={deleteRoutine}>
								Delete routine
							</button>
						</div>
					</div>
				</Modal>
			)}

			{isRoutineLoading ? (
				<AppLoader message='Getting this routine ready...' />
			) : routineLoadError ? (
				<div className={style.LoginText}>
					<p>{routineLoadError}</p>
					<SecondaryButton text='Go back' click={() => navigate(-1)} />
				</div>
			) : (
			<div className={style.RoutinePageWrapper}>
				<div className={style.SectionWrapper}>
					<div className={style.RoutineInfoWrapper}>
						<div className={style.RoutineNameWrapper}>
							<div className={style.RoutineNameRow}>
								<span className={style.RoutineName}>{routine.routineName}</span>
								{routine.isPrivate && <MdLock className={style.PrivateIcon} />}
							</div>
							<Link to={`/profile/${userID}`} className={style.RoutineCreator}>
								<span>
									Created by {routineUser.username || 'loading'}
									<VerifiedBadge userPlan={routineUser.userPlan} totalXP={routineUser.totalXP} size='small' />
								</span>
							</Link>
						</div>

						<span className={style.RoutineInfo}>
							Estimated completion time: {routine.estimatedTimeMinutes || routine.duration} mins
						</span>

						<div className={style.RoutineDetailsWrapper}>
							<div className={style.RoutineProgressWrapper}>
								<span className={style.RoutineProgress}>
									{currentProgress.toFixed(1)}% completed
								</span>
								<ProgressBar progress={currentProgress} />
							</div>

							<div className={style.RoutineStatsWrapper}>
								<Timer />
								<div className={style.RoutineXPWrapper}>
									<img src={cupIcon} alt="" className={style.RoutineXPIcon} />
									<span className={style.RoutineStatText}>{currentXP} XP</span>
								</div>
							</div>
						</div>

						<div className={style.QuickActions}>
							<button className={`${style.QuickActionBtn} ${isLiked ? style.LikedBtn : ''}`} onClick={toggleLike}>
								{isLiked ? <MdFavorite /> : <MdFavoriteBorder />}
								<span>{routine.likes?.length || 0}</span>
							</button>
							<button className={`${style.QuickActionBtn} ${isBookmarked ? style.BookmarkedBtn : ''}`} onClick={toggleBookmark}>
								{isBookmarked ? <MdBookmark /> : <MdBookmarkBorder />}
								<span>{isBookmarked ? 'Saved' : 'Save'}</span>
							</button>
							<button className={style.QuickActionBtn} onClick={openReminderModal}>
								<MdNotifications />
								<span>Remind Me</span>
							</button>
							<button className={style.QuickActionBtn} onClick={generateShareLink}>
								{copied ? <MdCheck /> : <MdShare />}
								<span>{copied ? 'Copied' : 'Share'}</span>
							</button>
							{isOwner && (
								<button className={style.QuickActionBtn} onClick={() => setShowInvite(!showInvite)}>
									<MdPersonAdd />
									<span>Invite</span>
								</button>
							)}
							{isOwner && (
								<button className={style.QuickActionBtn} onClick={togglePrivacy}>
									{routine.isPrivate ? <MdLockOpen /> : <MdLock />}
									<span>{routine.isPrivate ? 'Public' : 'Private'}</span>
								</button>
							)}
						</div>
					</div>

				{isOwner && showInvite && (
					<div className={style.InviteSection}>
						<input
							className={style.InviteInput}
							placeholder="Search people to invite..."
							value={inviteSearch}
							onChange={(e) => searchForInvite(e.target.value)}
						/>
						{inviteResults.map(person => (
							<div key={person.userID} className={style.InviteResult}>
								{person.profileImageLink ? (
									<img src={person.profileImageLink} alt="" className={style.InviteAvatar} />
								) : (
									<div className={style.InviteAvatarPlaceholder}>{person.username?.[0]?.toUpperCase()}</div>
								)}
								<span className={style.InviteUsername}>{person.username}</span>
								<button className={style.InviteSendBtn} onClick={() => sendInvite(person)}>
									<MdSend />
								</button>
							</div>
						))}
						<div className={style.EmailInviteDivider}>
							<span>or invite via email</span>
						</div>
						<div className={style.EmailInviteRow}>
							<MdMail className={style.EmailInviteIcon} />
							<input
								className={style.InviteInput}
								placeholder="friend@example.com"
								type="email"
								value={emailInvite}
								onChange={(e) => setEmailInvite(e.target.value)}
								onKeyDown={(e) => e.key === 'Enter' && sendEmailInvite()}
							/>
							<button
								className={style.InviteSendBtn}
								onClick={sendEmailInvite}
								disabled={emailInviteSending}
							>
								<MdSend />
							</button>
						</div>
						{shareURL && (
							<div className={style.ShareURLRow}>
								<span className={style.ShareURLText}>{shareURL}</span>
							</div>
						)}
					</div>
				)}

				{workouts.length > 0 && (
					<button className={style.StartSessionBtn} onClick={() => { setSessionActive(true); setSessionStartTime(Date.now()); }}>
						<MdPlayArrow className={style.StartSessionIcon} />
						<div className={style.StartSessionText}>
							<span className={style.StartSessionTitle}>Start Workout</span>
							<span className={style.StartSessionSub}>
								{workouts.length} exercise{workouts.length !== 1 ? 's' : ''} · {routine.estimatedTimeMinutes || '—'} min
							</span>
						</div>
					</button>
				)}

				{pendingAdaptation && (
					<AdaptationBanner
						adaptation={pendingAdaptation}
						onApply={applyAdaptation}
						onDismiss={dismissAdaptation}
						isApplying={isApplyingAdaptation}
					/>
				)}

			<div className={style.WorkoutListHeader}>
					<span className={style.WorkoutListTitle}>Exercises ({workouts.length})</span>
					{isOwner && (
						<div className={style.WorkoutListActions}>
							<button
								className={`${style.EditModeBtn} ${isEditMode ? style.EditModeBtnActive : ''}`}
								onClick={() => setIsEditMode(!isEditMode)}
							>
								{isEditMode ? 'Done' : 'Edit'}
							</button>
							<button
								className={style.AddWorkoutTrigger}
								onClick={() => setIsAddWorkoutOpen(!isAddWorkoutOpen)}
							>
								<MdAdd /> Add
							</button>
						</div>
					)}
				</div>

				{isAddWorkoutOpen && isOwner && (
					<div className={style.AddWorkoutForm}>
						<input
							className={style.AddWorkoutInput}
							placeholder="Exercise name (e.g. Barbell Back Squat)"
							value={newWorkoutType}
							onChange={(e) => setNewWorkoutType(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && addWorkoutManually()}
						/>
						<div className={style.AddWorkoutRow}>
							<div className={style.AddWorkoutField}>
								<label className={style.AddWorkoutLabel}>Sets</label>
								<input
									className={style.AddWorkoutSmallInput}
									type="number"
									min="1"
									value={newWorkoutSets}
									onChange={(e) => setNewWorkoutSets(e.target.value)}
								/>
							</div>
							<div className={style.AddWorkoutField}>
								<label className={style.AddWorkoutLabel}>Reps</label>
								<input
									className={style.AddWorkoutSmallInput}
									value={newWorkoutReps}
									onChange={(e) => setNewWorkoutReps(e.target.value)}
									placeholder="e.g. 8-12"
								/>
							</div>
							<button className={style.AddWorkoutSubmit} onClick={addWorkoutManually}>
								Add Exercise
							</button>
						</div>
					</div>
				)}

				<div className={style.WorkoutList}>
					{workouts.length > 0 ? (
						workouts.map((workout, i) => (
							<div key={`${workout.workoutType}-${i}`} className={style.WorkoutListItem}>
								<span className={style.WorkoutListNum}>{i + 1}</span>
								<div className={style.WorkoutListInfo}>
									<span className={style.WorkoutListName}>{workout.workoutType}</span>
									<span className={style.WorkoutListMeta}>
										{workout.workoutSets && `${workout.workoutSets} sets`}
										{workout.workoutSets && workout.workoutReps && ' · '}
										{workout.workoutReps && `${workout.workoutReps} reps`}
										{!workout.workoutSets && !workout.workoutReps && 'No details'}
									</span>
								</div>
								{isEditMode && isOwner && (
									<div className={style.WorkoutEditActions}>
										<button
											className={style.WorkoutMoveBtn}
											onClick={() => moveWorkout(i, -1)}
											disabled={i === 0}
											title="Move up"
										>
											<MdKeyboardArrowUp />
										</button>
										<button
											className={style.WorkoutMoveBtn}
											onClick={() => moveWorkout(i, 1)}
											disabled={i === workouts.length - 1}
											title="Move down"
										>
											<MdKeyboardArrowDown />
										</button>
										<button
											className={style.WorkoutDeleteBtn}
											onClick={() => removeWorkout(i)}
											title="Remove"
										>
											<MdDelete />
										</button>
									</div>
								)}
							</div>
						))
					) : (
						<span className={style.NoParticipants}>No workouts added yet.</span>
					)}
				</div>

				{isOwner && (
					<div className={style.AISuggestSection}>
						<div className={style.AISuggestControls}>
							<textarea
								className={style.AISuggestPrompt}
								placeholder="e.g. &quot;Focus on glutes&quot; or &quot;Add explosive movements&quot;"
								value={suggestionPrompt}
								onChange={(e) => setSuggestionPrompt(e.target.value)}
								rows={1}
								disabled={aiLoading}
								onInput={(e) => {
									e.target.style.height = 'auto';
									e.target.style.height = e.target.scrollHeight + 'px';
								}}
							/>
							<button className={style.AISuggestBtn} onClick={getAISuggestions} disabled={aiLoading}>
								<MdAutoAwesome />
								<span>{aiLoading ? 'Analyzing...' : 'Suggest'}</span>
							</button>
						</div>
						{showAISuggestions && (
							<div className={style.AISuggestContent}>
								{aiLoading ? (
									<div className={style.AISuggestLoading}>
										<span className={style.AIDot}></span>
										<span className={style.AIDot}></span>
										<span className={style.AIDot}></span>
									</div>
								) : (
									<>
										{getCleanAIText() && (
											<p className={style.AISuggestIntro}>{getCleanAIText()}</p>
										)}
										{parsedSuggestions.length > 0 && (
											<div className={style.SuggestionCards}>
												{parsedSuggestions.map((suggestion, i) => {
													const isSwap = suggestion.action === 'swap' && suggestion.replaces;
													return (
														<div key={i} className={style.SuggestionCard}>
															<div className={style.SuggestionCardHeader}>
																<div className={style.SuggestionCardInfo}>
																	<span className={style.SuggestionCardName}>{suggestion.workoutType}</span>
																	<span className={style.SuggestionCardMeta}>
																		{suggestion.workoutSets} sets · {suggestion.workoutReps} reps
																	</span>
																</div>
																{isSwap && (
																	<span className={style.SwapBadge}>
																		<MdSwapVert /> Swap
																	</span>
																)}
															</div>
															<p className={style.SuggestionCardReason}>{suggestion.reason}</p>
															{isSwap && (
																<p className={style.SuggestionReplaces}>
																	Replaces: <strong>{suggestion.replaces}</strong>
																</p>
															)}
															<div className={style.SuggestionCardActions}>
																{isSwap ? (
																	<button
																		className={style.SuggestionSwapBtn}
																		onClick={() => swapWorkoutWithSuggestion(suggestion)}
																	>
																		<MdSwapVert /> Swap In
																	</button>
																) : (
																	<button
																		className={style.SuggestionAddBtn}
																		onClick={() => addSingleSuggestion(suggestion)}
																	>
																		<MdAdd /> Add to Routine
																	</button>
																)}
																<button
																	className={style.SuggestionDismissBtn}
																	onClick={() => setParsedSuggestions(prev => prev.filter((_, idx) => idx !== i))}
																>
																	<MdClose />
																</button>
															</div>
														</div>
													);
												})}
												{parsedSuggestions.length > 1 && (
													<button className={style.AIAddAllBtn} onClick={addAllSuggestions}>
														Apply all suggestions
													</button>
												)}
											</div>
										)}
										{parsedSuggestions.length === 0 && !getCleanAIText() && (
											<p className={style.AISuggestIntro}>No suggestions available. Try again.</p>
										)}
									</>
								)}
							</div>
						)}
					</div>
				)}
				</div>

				<div className={style.SideSection}>
					<div className={style.RoutineActionsWrapper}>
						{userID !== user.id && participants ? (
							participants.some(p => p.userID === user.id) ? (
								<PrimaryButton text="Leave" click={() => leaveRoutine(userID, routineName, user.id)} />
							) : (
								<PrimaryButton text="Join" click={() => joinRoutine(userID, routineName, user.id, user.username, user.profileImageUrl)} />
							)
						) : null}
					</div>

					<span className={style.RoutineSectionHeading}>Participants</span>
					<div className={style.ParticipantsGrid}>
						{participants.length > 0 ? (
							<>
								{participants
									.sort((a, b) => b.userRoutineXP - a.userRoutineXP)
									.slice(0, 6)
									.map((participant, idx) => (
										<Link to={`/profile/${participant.userID}`} key={participant.userID} className={style.ParticipantCard}>
											<span className={style.ParticipantRank}>#{idx + 1}</span>
											{participant.profileImageLink && !failedAvatars[`participant-${participant.userID}`] ? (
												<img
													src={participant.profileImageLink}
													alt=""
													className={style.ParticipantAvatar}
													onError={() => setFailedAvatars(prev => ({ ...prev, [`participant-${participant.userID}`]: true }))}
												/>
											) : (
												<div className={style.ParticipantAvatarPlaceholder}>
													{participant.username?.[0]?.toUpperCase()}
												</div>
											)}
											<div className={style.ParticipantInfo}>
												<span className={style.ParticipantName}>
													{participant.userID === user.id ? 'You' : participant.username}
												</span>
												<span className={style.ParticipantXP}>
													{(participant.userID === user.id ? participant.userRoutineXP + currentXP : participant.userRoutineXP)} XP
												</span>
											</div>
										</Link>
									))}
							</>
						) : (
							<p className={style.NoParticipants}>No participants yet</p>
						)}
					</div>

					{participants.length > 6 && (
						<SecondaryButton text="View all participants" click={() => setIsLeaderboardsOpen(true)} />
					)}

					<SecondaryButton text="View leaderboards" click={() => setIsLeaderboardsOpen(true)} />

					{isOwner && adaptationHistory.length > 0 && (
						<AdaptationHistory history={adaptationHistory} />
					)}

					{isOwner && (
						<button className={style.DeleteButton}
							onClick={() => setIsDeleteModalOpen(true)}
						>
							Delete routine
						</button>
					)}

					<div className={style.CommentsSection}>
						<span className={style.RoutineSectionHeading}>
							<MdChatBubbleOutline style={{ verticalAlign: 'middle', marginRight: 6 }} />
							Comments ({comments.length})
						</span>

						{comments.length > 3 && !showAllComments && (
							<button className={style.ViewAllBtn} onClick={() => setShowAllComments(true)}>
								View all {comments.length} comments
							</button>
						)}

						<div className={style.CommentsList}>
							{visibleComments.map((comment, i) => (
								<div key={i} className={style.CommentItem}>
									<Link to={`/profile/${comment.userID}`} className={style.CommentAvatar}>
										{comment.profileImageLink && !failedAvatars[`comment-${comment.userID}-${i}`] ? (
											<img
												src={comment.profileImageLink}
												alt=""
												onError={() => setFailedAvatars(prev => ({ ...prev, [`comment-${comment.userID}-${i}`]: true }))}
											/>
										) : (
											<span>{comment.username?.[0]?.toUpperCase()}</span>
										)}
									</Link>
									<div className={style.CommentContent}>
										<Link to={`/profile/${comment.userID}`} className={style.CommentAuthor}>
											{comment.username}
										</Link>
										<p className={style.CommentText}>{comment.text}</p>
									</div>
								</div>
							))}
						</div>

						<div className={style.CommentInputRow}>
							<input
								className={style.CommentInput}
								placeholder="Add a comment..."
								value={commentText}
								onChange={(e) => setCommentText(e.target.value)}
								onKeyDown={(e) => e.key === 'Enter' && addComment()}
							/>
							{commentText.trim() && (
								<button className={style.CommentSendBtn} onClick={addComment}>
									<MdSend />
								</button>
							)}
						</div>
					</div>
				</div>
			</div>
			)}
		</div>
	);
};

export default RoutinePage;
